import type { InsurancePackage } from '@/models/insuranceApplication'
import { packageCartKeys, type PackageLine, type QuotePackage } from './PackageCard'
import { isCartBlocked, type PricedQuoteCard, type QuoteCardData, QuoteCardStage } from './quoteCardTypes'
import { cartTotal, groupCartByQuote } from './quoteCartItems'

/** Whether a card's Add to cart works today: the blocks QuoteCard puts on its own button. */
export function isPackageableCard(card: QuoteCardData): card is PricedQuoteCard {
  return (
    card.stage === QuoteCardStage.PRICED &&
    !card.failedStage &&
    !card.isCustomerUploaded &&
    !isCartBlocked(card, card.indicationStatus)
  )
}

/**
 * The package as the page draws it, read off each offer's own card so every line keeps its own
 * estimate-or-quote label, and priced the way the cart prices the same keys. The backend only sends
 * customers a package whose offers are all still available; this also drops one an offer's card
 * won't let into the cart.
 */
export function applicationToPackage(
  quotePackage: InsurancePackage | null | undefined,
  offers: QuoteCardData[],
  /** The page's merged cards. The cart drops keys whose card it can't add, so each offer's card must pass too. */
  quotes: QuoteCardData[]
): QuotePackage | null {
  if (!quotePackage?.indicationIds.length || quotePackage.withdrawnReason) return null

  const lines: PackageLine[] = []
  for (const indicationId of quotePackage.indicationIds) {
    const offer = offers.find((card) => card.id === indicationId)
    if (!offer || !isPackageableCard(offer) || !offer.coverages.length) return null
    const card = quotes.find((quote) => (quote.memberIds ?? [quote.id]).includes(indicationId))
    if (!card || !isPackageableCard(card)) return null
    offer.coverages.forEach((coverage) =>
      lines.push({
        quoteId: offer.id,
        carrierName: offer.carrierName,
        carrierProgram: offer.carrierProgram,
        sourcedThrough: offer.sourcedThrough,
        iconUrl: offer.iconUrl,
        iconAlt: offer.iconAlt,
        pricingKind: offer.pricingKind,
        coverage,
      })
    )
  }
  const inCart = groupCartByQuote(new Set(packageCartKeys({ lines })), quotes)
  return {
    id: quotePackage.id,
    lines,
    oneTimeFees: inCart.flatMap((group) => group.oneTimeFees),
    total: cartTotal(inCart),
  }
}
